/**
 * Scoped Storage Wrapper for Redux Persist
 *
 * This wrapper intercepts localStorage operations and dynamically adds
 * owner-based scoping at runtime. This solves the problem of persist configs
 * being created before owner is available.
 *
 * Instead of using keyPrefix (which is static), we intercept getItem/setItem
 * and add the scope dynamically based on the current owner.
 */

import { type WebStorage } from 'redux-persist/lib/types';

import { getLegacyKey, getOwner, getScopedKey } from './owner';

/**
 * Creates a scoped storage wrapper that dynamically adds owner prefix
 *
 * @param baseStorage - The underlying storage (typically localStorage)
 * @returns Scoped storage that implements redux-persist's storage interface
 */
export function createScopedStorage(baseStorage: WebStorage): WebStorage {
  /**
   * Helper to extract slice key from redux-persist key
   * Redux-persist passes keys like "persist:root" or "persist:feature_X"
   * But getScopedKey/getLegacyKey expect just "root" or "feature_X"
   */
  const extractSliceKey = (persistKey: string): string => {
    if (persistKey.startsWith('persist:')) {
      return persistKey.substring('persist:'.length);
    }
    return persistKey;
  };

  return {
    /**
     * Get item with dynamic scoping
     * If owner exists, use scoped key. Otherwise, fall back to legacy key.
     */
    getItem: async (key: string): Promise<string | null> => {
      const owner = getOwner();
      const sliceKey = extractSliceKey(key);

      if (owner) {
        // Owner available - use scoped key
        const scopedKey = getScopedKey(owner, sliceKey);
        const value = baseStorage.getItem(scopedKey);

        if (process.env.NODE_ENV !== 'production') {
          console.log(`[ScopedStorage] getItem: ${key} → ${scopedKey}`, {
            found: !!value,
            owner: `${owner.env}:${owner.userId}`,
          });
        }

        return value;
      }

      // No owner yet - use legacy unscoped key (for backward compatibility during initial load)
      const legacyKey = getLegacyKey(sliceKey);
      const value = baseStorage.getItem(legacyKey);

      if (process.env.NODE_ENV !== 'production') {
        console.log(`[ScopedStorage] getItem (no owner): ${key} → ${legacyKey}`, {
          found: !!value,
        });
      }

      return value;
    },

    /**
     * Set item with dynamic scoping
     * Only write if owner exists (don't persist before authentication)
     */
    setItem: async (key: string, value: string): Promise<void> => {
      const owner = getOwner();
      const sliceKey = extractSliceKey(key);

      if (!owner) {
        // No owner - skip persistence
        if (process.env.NODE_ENV !== 'production') {
          console.log(`[ScopedStorage] setItem blocked (no owner): ${key}`);
        }
        return;
      }

      // Owner available - use scoped key
      const scopedKey = getScopedKey(owner, sliceKey);

      if (process.env.NODE_ENV !== 'production') {
        console.log(`[ScopedStorage] setItem: ${key} → ${scopedKey}`, {
          owner: `${owner.env}:${owner.userId}`,
        });
      }

      baseStorage.setItem(scopedKey, value);
    },

    /**
     * Remove item with dynamic scoping
     */
    removeItem: async (key: string): Promise<void> => {
      const owner = getOwner();
      const sliceKey = extractSliceKey(key);

      if (owner) {
        const scopedKey = getScopedKey(owner, sliceKey);

        if (process.env.NODE_ENV !== 'production') {
          console.log(`[ScopedStorage] removeItem: ${key} → ${scopedKey}`);
        }

        baseStorage.removeItem(scopedKey);
      } else {
        // Fallback to legacy key if no owner
        const legacyKey = getLegacyKey(sliceKey);

        if (process.env.NODE_ENV !== 'production') {
          console.log(`[ScopedStorage] removeItem (no owner): ${key} → ${legacyKey}`);
        }

        baseStorage.removeItem(legacyKey);
      }
    },
  };
}
